import React, { useRef, useEffect, useContext, useState } from 'react';
import { CheckboxField } from '@trp-beacon-ui-kit/react-controls';
import { FlowContext } from '../../core/flow-engine';
import styles from './style.css';
import {
  CheckboxMessage,
  DocsAndDiscMessage,
  ButtonSectionRenderer,
  toggleElectronicAccessModal,
  docAndDiscErrorText
} from './utils';
import TermsRenderer from '../TermsRenderer/TermsRenderer';
import SectionHeading from '../Typography/SectionHeading/SectionHeading';
import DocumentsDisclosureRenderer from '../DocumentsDisclosureRenderer/DocumentsDisclosureRenderer';
import TaxpayerCertificationsRenderer from '../TaxpayerCertificationsRenderer/TaxpayerCertificationsRenderer';
import RetirementIncomeTaxWitholding from '../RetirementIncomeTaxWithholding/RetirementIncomeTaxWithholding';
import { accountTypes, isTaxable } from '../../flows/shared/utils';
import { withDefault } from '../../core/util';
import PaperlessTermsModal from '../PaperlessTermsModal/PaperlessTermsModal';
import { generateGeneralTestID } from '../../generateAutomationTestIDs';

/* eslint-disable complexity */
const TermsConditionsRenderer = ({
  accountType,
  termsData,
  hasRetirementIncomeFund = false
}) => {
  const { flowProps, formProps, next } = useContext(FlowContext);
  const termsWrapperRef = useRef(null);

  const { renderTaxpayerCertifications } = flowProps;

  const electronicAccessDelivery = termsData?.electronicAccessDelivery;
  const electronicAccessDeliveryTermsID = withDefault(
    electronicAccessDelivery?.electronicAccessDeliveryTermsID,
    ''
  );
  const electronicAccessDeliveryTermsParagraphs = withDefault(
    electronicAccessDelivery?.electronicAccessDeliveryTermsParagraphs,
    ''
  );
  const termsAEMContent = withDefault(
    termsData?.electronicAccessDelivery?.aemTermsContent,
    false
  );

  const needsTaxWithholdingForSubmit =
    [accountTypes.TRANSFER, accountTypes.ROLLOVER].includes(accountType) &&
    hasRetirementIncomeFund;

  const [
    electronicAccessDeliveryConsent,
    setElectronicAccessDeliveryConsent
  ] = useState(false);
  const [docsAndDiscClick, setDocsAndDiscClick] = useState(false);
  const [submitBtnClick, setSubmitBtnClick] = useState(false);

  useEffect(() => {
    const { current } = termsWrapperRef;
    if (!current) return;

    /* eslint no-param-reassign: ["error", { "props": true, "ignorePropertyModificationsFor": ["link"] }] */
    [...current.querySelectorAll('a')].forEach(link => {
      // if paperless terms are avaialble, paperless terms link will open a modal on click
      if (
        termsAEMContent &&
        link?.innerHTML?.toLowerCase()?.includes('paperless')
      ) {
        link.onclick = e => {
          e.preventDefault();
          toggleElectronicAccessModal();
        };
      } else if (link?.innerHTML?.toLowerCase()?.includes('paperless')) {
        /* set element properties for accessibility when paperless
         * terms fail to load
         */
        link.href = '';
        link.role = 'link';
        link.ariaDisabled = 'true';
        link.setAttribute(
          'style',
          'color: #000000; pointer-events: none; text-decoration: none;'
        );
      }
      // All other links except for paperless terms should open inside of a new tab.
      else {
        link.target = '_blank';
      }
    });
  }, []);

  const checkboxText =
    'I acknowledge and agree to the terms and conditions of the Consent to Electronic Access and Delivery, the T. Rowe Price U.S. and Canada Privacy Notice, the Customer Account Agreement, and the T. Rowe Price Traditional and Roth IRA Disclosure Statement and Custodial Agreement (applicable only to IRA accounts) contained in this online account application.';

  return (
    <div>
      {needsTaxWithholdingForSubmit && (
        <div className="review-section">
          <RetirementIncomeTaxWitholding />
        </div>
      )}
      <div className="wrapper">
        <SectionHeading text="Terms and Conditions" />
        <div>
          <div
            key="1"
            id="electronicAccessTerms"
            data-name={generateGeneralTestID('Review', 'ElectronicAccessTerms')}
            ref={termsWrapperRef}>
            <p>
              To complete this online account application, you must review and
              consent to the electronic access and delivery requirements and
              agree to the terms and conditions of the Customer Account
              Agreement in the link below. &quot;I&quot; or &quot;we&quot;
              refers to all account owners.
            </p>
            <TermsRenderer
              terms={electronicAccessDeliveryTermsParagraphs}
              termname="electronicAccessDelivery"
            />
          </div>
          <div key="docs-disc-1" id="instruction">
            <p>II. Documents and Disclosures</p>
            {isTaxable(accountType) && (
              <p className="docs-disc-instruction">
                1. You must open and review the following Customer Account
                Agreement to complete the online account opening process:
              </p>
            )}
            {!isTaxable(accountType) && (
              <p className="docs-disc-instruction">
                1. You must open and review the following T. Rowe Price
                Traditional and Roth IRA Disclosure Statement and Custodial
                Agreement and Customer Account Agreement to complete the online
                account opening process: 
              </p>
            )}
          </div>
          <div>
            <DocsAndDiscMessage
              submitBtnClick={submitBtnClick}
              docsAndDiscClick={docsAndDiscClick}
              message={docAndDiscErrorText(accountType)}
            />
          </div>
          <DocumentsDisclosureRenderer
            accountType={accountType}
            setDocsAndDiscClick={setDocsAndDiscClick}
            docsAndDiscClick={docsAndDiscClick}
          />
          {renderTaxpayerCertifications && (
            <div key="3" id="taxpayerCertifications">
              <TaxpayerCertificationsRenderer formProps={formProps} />
            </div>
          )}
        </div>

        <div key="docs-disc-2" id="instruction">
          <p className="docs-disc-instruction">
            2. You must acknowledge and agree to the terms and conditions
            specified above.
          </p>
        </div>
        <CheckboxMessage
          consented={electronicAccessDeliveryConsent}
          submitBtnClick={submitBtnClick}
        />
        <div className="checkbox-container">
          <CheckboxField
            className="electronicAccessDeliveryConsent"
            title={<div className="cell">{checkboxText}</div>}
            disabled={!docsAndDiscClick}
            value={electronicAccessDeliveryTermsID}
            onChange={() =>
              setElectronicAccessDeliveryConsent(
                !electronicAccessDeliveryConsent
              )
            }
          />
        </div>

        <ButtonSectionRenderer
          electronicAccessDeliveryConsent={electronicAccessDeliveryConsent}
          docsAndDiscClick={docsAndDiscClick}
          setSubmitBtnClick={setSubmitBtnClick}
          next={next}
        />

        <PaperlessTermsModal termsAEMContent={termsAEMContent} />
        <style jsx>{styles}</style>
      </div>
    </div>
  );
};

export default TermsConditionsRenderer;
